import { Box, Button, TextField, Grid, Chip } from '@mui/material';
import { useState, useRef, useCallback } from 'react';
import { KEYBOARD_KEYS } from '@constants';
import { format, sub } from 'date-fns';
import EllipsisText from 'components/EllipsisText';
import { pickBy, identity } from 'lodash';
import { useMount } from 'react-use';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import useSandboxCreatorSelector from 'SandboxModule/hooks/useSandboxCreatorSelector';
import useSandboxStatusSelector from 'SandboxModule/hooks/useSandboxStatusSelector';
import DateRangeOptions from 'components/DateRangeOptions';
import { DATE_RANGE_OPTIONS, DateRangeOptionsValuesType } from '@constants';
import { useEventSubscription } from 'core/event/useEventSubscription';
import { AppEvents } from 'core/event/constants';

const chipWidth = '100px';
const defaultStartDate = format(sub(new Date(), { months: 1 }), 'yyyy-MM-dd');
const defaultEndDate = format(new Date(), 'yyyy-MM-dd');

export const useSandboxFilters = () => {
  const [sandboxFilter, setSandboxFilter] = useState({
    teamName: '',
    realmId: '',
    includeArchived: true,
  });

  const [dayRange, setDayRange] = useState<{
    startDate: string | null | undefined;
    endDate: string | null | undefined;
    currentDateRangeOption: DateRangeOptionsValuesType;
  }>({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    currentDateRangeOption: DATE_RANGE_OPTIONS.CUSTOM,
  });
  const { selectedStatuses, SandboxStatusSelector, handleClearSelectedStatuses } =
    useSandboxStatusSelector();
  const {
    selectedCreators,
    handleClearSelectedCreators,
    SandboxCreatorSelector,
    fetchSandboxCreators,
  } = useSandboxCreatorSelector();
  const [tags, setChipList] = useState<Array<string>>([]);
  const chipInputRef = useRef(document.createElement('input'));
  const { fetchSandboxes } = useSandboxes();

  useMount(() => {
    fetchSandboxCreators();
  });
  const onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setSandboxFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onDateRangeChange: Parameters<typeof DateRangeOptions>[0]['onDateRangeChange'] = ({
    newEnd,
    newStart,
    newOption,
  }) => {
    setDayRange((prev) => ({
      ...prev,
      startDate: newStart,
      endDate: newEnd,
      currentDateRangeOption: newOption,
    }));
  };

  const handleAddTag = (value: string) => {
    setChipList((prev) => {
      if (prev.some((chip) => chip === value)) return prev;

      const nextTagsState = [...prev, value];
      return nextTagsState;
    });
    chipInputRef.current.value = '';
  };

  const onTagsInputChange = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = e;
    const search = chipInputRef.current.value.trim();
    if (!search) return;
    if (key === KEYBOARD_KEYS.ENTER) {
      handleAddTag(search);
    }
  };

  const handleTagDelete = (value: string) => () => {
    setChipList((prev) => {
      const nextTagsState = prev.filter((chip) => chip !== value);

      return nextTagsState;
    });
  };

  const renderChip = (chip: string) => {
    return (
      <EllipsisText text={chip} width={chipWidth} key={`${chip}`}>
        <Chip
          color="primary"
          label={chip}
          onDelete={handleTagDelete(chip)}
          style={{ maxWidth: `calc(${chipWidth} - 10%)` }}
        />
      </EllipsisText>
    );
  };

  const onSearch = () => {
    const sandboxFilterParams = pickBy(
      {
        includeArchived: sandboxFilter.includeArchived,
        createdAtStartDate: dayRange.startDate,
        createdAtEndDate: dayRange.endDate,
        realmIdSearchKey: sandboxFilter.realmId,
        teamNameSearchKey: sandboxFilter.teamName,
        statuses: selectedStatuses,
        creators: selectedCreators,
        tags: tags,
      },
      identity,
    );
    fetchSandboxes(SandboxIsoStateIds.SandboxTable, sandboxFilterParams);
  };

  useEventSubscription({
    callback: useCallback(() => {
      fetchSandboxes(SandboxIsoStateIds.SandboxTable);
    }, [fetchSandboxes]),
    eventName: AppEvents.CreateRealmSuccess,
  });

  const clearFilters = () => {
    setSandboxFilter({
      teamName: '',
      realmId: '',
      includeArchived: true,
    });
    setChipList([]);
    chipInputRef.current.value = '';
    handleClearSelectedStatuses();
    handleClearSelectedCreators();
    onDateRangeChange({
      newOption: DATE_RANGE_OPTIONS.CUSTOM,
      newStart: defaultStartDate,
      newEnd: defaultEndDate,
    });
    fetchSandboxes(SandboxIsoStateIds.SandboxTable);
  };

  return {
    clearFilters,
    SandboxFilter: (
      <Grid
        container
        spacing={4}
        style={{ padding: '10px' }}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <Grid size={3}>
          <TextField
            className="filter-fields"
            label="Team Name"
            name="teamName"
            onChange={onFilterChange}
            value={sandboxFilter.teamName}
            fullWidth
          />
        </Grid>
        <Grid size={3}>
          <TextField
            className="filter-fields"
            label="Realm ID"
            name="realmId"
            onChange={onFilterChange}
            value={sandboxFilter.realmId}
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <DateRangeOptions
            dateRangeOptions={[DATE_RANGE_OPTIONS.CUSTOM]}
            defaultOption={DATE_RANGE_OPTIONS.CUSTOM}
            onDateRangeChange={onDateRangeChange}
            defaultCustomStart={defaultStartDate}
            defaultCustomEnd={defaultEndDate}
            customStartDateParent={dayRange.startDate}
            customEndDateParent={dayRange.endDate}
          />
        </Grid>
        <Grid size={3}>
          <TextField
            id="tags"
            label="Tags"
            name="tags"
            type="text"
            onKeyDown={onTagsInputChange}
            inputRef={chipInputRef}
            fullWidth
          />
        </Grid>
        <Grid size={3}>{SandboxStatusSelector}</Grid>
        <Grid size={3}>{SandboxCreatorSelector}</Grid>
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '3px',
            }}
          >
            {tags.map((chip) => renderChip(chip))}
          </Box>
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              gap: '5px',
              justifyContent: 'flex-end',
            }}
          >
            <Button variant="contained" color="primary" onClick={onSearch}>
              Search
            </Button>
            <Button variant="contained" color="primary" onClick={clearFilters}>
              Clear
            </Button>
          </Box>
        </Grid>
      </Grid>
    ),
  };
};
